// src/components/PortfolioAnalytics.js
import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const PortfolioAnalytics = ({ portfolio, darkMode }) => {
  // Calculate sector allocation
  const sectorAllocation = portfolio.reduce((acc, stock) => {
    const sector = getSector(stock.symbol);
    acc[sector] = (acc[sector] || 0) + stock.marketValue;
    return acc;
  }, {});

  const sectorData = Object.entries(sectorAllocation).map(([name, value]) => ({
    name,
    value
  }));

  // Performance over time data (mock)
  const performanceData = [
    { date: 'Jan', return: 2.5 },
    { date: 'Feb', return: 5.2 },
    { date: 'Mar', return: -1.8 },
    { date: 'Apr', return: 3.4 },
    { date: 'May', return: 7.1 },
    { date: 'Jun', return: 4.8 }
  ];

  const fetchRiskAnalysis = async (portfolio) => {
    try {
      const response = await fetch('http://localhost:8000/api/portfolio/risk-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(portfolio),
      });

      if (!response.ok) {
        throw new Error('Risk analysis failed');
      }

      return await response.json();
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  };    

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  return (
    <div className={`rounded-lg shadow-lg p-6 ${
      darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
    }`}>
      <h3 className="text-2xl font-bold mb-6">Portfolio Analytics</h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sector Allocation Pie Chart */}
        <div className={`p-4 rounded-lg ${
          darkMode ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <h4 className="text-lg font-semibold mb-4">Sector Allocation</h4>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={sectorData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
              >
                {sectorData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Value']} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Performance Line Chart */}
        <div className={`p-4 rounded-lg ${
          darkMode ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <h4 className="text-lg font-semibold mb-4">Monthly Performance</h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="return" 
                stroke="#8884d8" 
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Performers Bar Chart */}
        <div className={`p-4 rounded-lg ${
          darkMode ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <h4 className="text-lg font-semibold mb-4">Top Performers</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={portfolio.slice(0, 5)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="symbol" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="unrealizedPnl" fill="#00C49F" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Risk Analysis */}
        <div className={`p-4 rounded-lg ${
          darkMode ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <h4 className="text-lg font-semibold mb-4">Risk Analysis</h4>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm">
                <span>Portfolio Beta</span>
                <span className="font-semibold">1.23</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '65%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm">
                <span>Sharpe Ratio</span>
                <span className="font-semibold">1.45</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '72%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm">
                <span>Volatility</span>
                <span className="font-semibold">18.2%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-red-500 h-2 rounded-full" style={{ width: '45%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get sector for symbol
const getSector = (symbol) => {
  const sectors = {
    'AAPL': 'Technology',
    'GOOGL': 'Technology',
    'MSFT': 'Technology',
    'TSLA': 'Automotive',
    'AMZN': 'E-Commerce',
    'NVDA': 'Semiconductors',
    'JPM': 'Financial',
    'V': 'Financial',
    'DIS': 'Entertainment',
    'NFLX': 'Entertainment',
    'COIN': 'Cryptocurrency',
    'CRCG': 'Cryptocurrency',
    'CRWG': 'AI Infrastructure',
    'WULF': 'AI Infrastructure',
    'XRPT': 'Cryptocurrency'
  };
  return sectors[symbol] || 'Other';
};

export default PortfolioAnalytics;